<template>
  <div class="p-relative reply-thread">
    <template
      v-if="
        !message.reply_thread_id ||
          (isPresent(messageThread) && messageThread.message_count === 0)
      "
    >
      <v-link
        :to="isPresent(messageThread) ? replyThreadUrl : newThreadUrl"
        class="d-flex"
      >
        <v-icon v-if="isFeed" icon="forum" class="reply-thread__icon" />
        <small
          class="ml-1 color-brand font-weight-semi-bold d-flex align-items-center"
        >
          Reply
        </small>
      </v-link>
    </template>
    <template v-else-if="isLoading">
      <div class="d-flex align-items-center">
        <placeholder-rect height="32px" width="32px" class="m-0" />
        <placeholder-rect height="18px" width="60px" class="m-0 ml-1" />
      </div>
    </template>
    <template v-else-if="messageThread">
      <v-link :to="replyThreadUrl" class="d-flex align-items-center">
        <subscriber-avatars :subscribers="participants.map(p => p.user)" />
        <small class="ml-1 color-brand font-weight-semi-bold">
          <transition name="fade" mode="out-in">
            <span :key="messageThread.message_count">
              {{ messageThread.message_count }}&nbsp;{{ pluralReply }}
            </span>
          </transition>
        </small>

        <transition name="fade" mode="out-in">
          <small
            v-if="latestMessage"
            :key="latestMessage.message_type + latestMessage.message_ts"
            class="reply-thread__message text-grey-60 ml-1"
          >
            {{
              latestMessage.message_type === MESSAGE_TYPES.VIDEO
                ? "Sent a video"
                : latestMessage.message_type === MESSAGE_TYPES.IMAGE
                ? "Sent an image"
                : latestMessage.message_type === MESSAGE_TYPES.ATTACHMENT
                ? latestMessage.attachment_data.url
                : latestMessage.message
            }}
          </small>
        </transition>
      </v-link>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex"

import { MESSAGE_TYPES } from "@/lib/pusher-utils"
import { isPresent } from "@/lib/utils"
import { buildMessageId } from "@/lib/messages-utils"

import PlaceholderRect from "@/components/PlaceholderRect"

export default {
  components: {
    PlaceholderRect,
    SubscriberAvatars: () => import("@/components/SubscriberAvatars")
  },

  props: {
    message: { type: Object, required: true },
    isFeed: { type: Boolean, default: false }
  },

  data() {
    return {
      replyThreadSubscription: null,
      isLoading: false,
      MESSAGE_TYPES
    }
  },

  computed: {
    ...mapGetters("messages", ["getMessageThread"]),
    ...mapGetters("auth", ["isAuthenticated"]),

    replyThreadId() {
      return this.message.reply_thread_id
    },

    messageThread() {
      return this.getMessageThread(this.replyThreadId)
    },

    latestMessage() {
      return this.messageThread?.latest_message
    },

    participants() {
      return this.messageThread?.participants || []
    },

    pluralReply() {
      return this.messageThread.message_count > 1 ? "Replies" : "Reply"
    },

    threadSubscriptionParams() {
      return {
        subscriptionId: `reply-thread-${this.replyThreadId}`,
        messageThreadId: this.replyThreadId
      }
    },

    replyThreadUrl() {
      return `/me/messages/${this.replyThreadId}?reply=true`
    },

    newThreadUrl() {
      return `/me/messages/new?message=${buildMessageId(this.message)}`
    }
  },

  methods: {
    isPresent,

    ...mapActions("messages", [
      "fetchMessageThread",
      "subscribeToThread",
      "unsubscribeFromThread"
    ]),
    ...mapMutations("messages", ["setMessageThread"]),

    loadThread() {
      if (this.messageThread) {
        this.subscribeToThread(this.threadSubscriptionParams)
      } else if (this.replyThreadId) {
        this.isLoading = !this.messageThread
        this.fetchMessageThread(this.replyThreadId)
          .then(() => {
            if (this.isAuthenticated) {
              this.subscribeToThread(this.threadSubscriptionParams)
            }
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },

    teardownThread() {
      this.unsubscribeFromThread(this.threadSubscriptionParams)
    }
  },

  beforeDestroy() {
    this.teardownThread()
  },

  deactivated() {
    this.teardownThread()
  },

  activated() {
    this.loadThread()
  },

  mounted() {
    this.loadThread()
  },

  watch: {
    replyThreadId() {
      if (this.replyThreadId) {
        this.loadThread()
      }
    },

    isAuthenticated() {
      if (this.isAuthenticated) {
        this.loadThread()
        this.fetchMessageThread(this.replyThreadId) // This ensures the currentParticipant is updated
      }
    }
  }
}
</script>

<style lang="scss">
@import "@/assets/styles/variables";
@import "@/assets/styles/mixins";

.reply-thread {
  margin-bottom: 0.5rem;
  margin-left: 2.3rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 6px;

  .reply-thread__message {
    @include overflow-ellipsis;
  }
}

.reply-thread__icon {
  font-size: $font-size--large;
}
</style>
